import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames/bind';

import {
    FormControl, FormLabel, Input, Select, MenuItem, FormHelperText, InputAdornment, IconButton, makeStyles,
} from '@material-ui/core';

import { ClearIcon } from '../icons';

const combineClasses = (classes1, classes2) => {
    if (!classes1) { return classes2; }
    if (!classes2) { return classes1; }

    return [...Object.keys(classes1), ...Object.keys(classes2)]
        .reduce((prev, current) => ({ ...prev, [current]: classnames(classes1[current], classes2[current]) }), {});
};

const useStyles = makeStyles((theme) => {
    const inputCommon = {
        marginLeft: theme.spacing(2),
        flex: '1 1 auto',
        '&$disabled': {
            color: theme.palette.common.black,
        },
    };

    return {
        root: {
            background: theme.palette.common.white,
            padding: theme.spacing(2),
            border: theme.borders.thickSecondary,
            borderColor: theme.colors.transparent,

            '&:focus-within': {
                border: theme.borders.thickSecondary,

                '& $endAdornment': {
                    visibility: 'visible',
                },
            },
        },
        error: {
            border: theme.borders.normal,
            borderColor: theme.palette.error.main,
        },
        inner: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
        },
        label: {
            '&$focused': {
                color: theme.palette.grey[600],
            },
        },
        inputText: {
            ...inputCommon,
        },
        inputSelect: {
            ...inputCommon,
            // Overriding margin added by MUI for inputs with labels
            'label + &': { marginTop: 0 },
        },
        inputForm: {
            // Overriding margin added by MUI for inputs with labels
            'label + &': { marginTop: 0 },
        },
        endAdornment: {
            visibility: 'hidden',
        },
        inputInput: {
            padding: 0,
        },
        selectInput: {
            padding: `0 ${theme.spacing(3)}px 0 0`,
            '&:focus': {
                backgroundColor: theme.palette.common.white,
            },
        },
        focused: {
        },
        disabled: {
        },
    };
});

const VimoInput = ({
    className, hideIfNoValue, id, label, value, type, readOnly, selects, onChange, InputProps, validationError,
    autoFocus, onClear, defaultText, multiline, onEnter, inputSuffix, labelNoTranslate,
}) => {
    const classes = useStyles();
    const translate = useSelector(state => getTranslate(state.localize));

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            onEnter();
        }
    };

    if (readOnly && hideIfNoValue && !value) {
        return null;
    }

    const formLabel = labelNoTranslate || (label && `${translate(label)}:`);

    return (
        <FormControl
            className={classnames(classes.root, className, { [classes.error]: !!validationError })}
            error={!!validationError}
            fullWidth
        >
            <div className={classes.inner}>
                {formLabel && (
                    <FormLabel
                        className={classes.label}
                        classes={{ focused: classes.focused }}
                        htmlFor={id}
                    >
                        {formLabel}
                    </FormLabel>
                )}

                { readOnly || !selects || selects.length === 0 ? (
                    <Input
                        className={classes.inputText}
                        id={id}
                        value={(selects && (selects.find(v => v.key === value) || {}).value) || value || ''}
                        disableUnderline
                        type={type}
                        disabled={readOnly}
                        onChange={onChange}
                        classes={combineClasses(
                            { formControl: classes.inputForm, disabled: classes.disabled, input: classes.inputInput },
                            InputProps && InputProps.classes,
                        )}
                        autoFocus={autoFocus}
                        endAdornment={inputSuffix ? (
                            <InputAdornment position="end">
                                {inputSuffix}
                            </InputAdornment>
                        ) : onClear && !readOnly && (
                            <InputAdornment position="end" className={classes.endAdornment}>
                                <IconButton
                                    aria-label="Clear"
                                    onClick={onClear}
                                >
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )}
                        placeholder={defaultText}
                        multiline={multiline}
                        minRows={5}
                        onKeyPress={onEnter ? handleKeyPress : null}
                        {...(InputProps || {})}
                    />
                ) : (
                    <Select
                        className={classes.inputSelect}
                        value={value || ''}
                        input={<Input id={id} />}
                        autoWidth
                        disableUnderline
                        displayEmpty
                        onChange={onChange}
                        classes={{
                            selectMenu: classes.selectInput,
                        }}
                    >
                        {selects.map(item => (
                            <MenuItem key={item.key} value={item.key}>
                                {item.value}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </div>
            {validationError && (
                <FormHelperText>{translate(validationError)}</FormHelperText>
            )}
        </FormControl>
    );
};

VimoInput.propTypes = {
    className: PropTypes.string,
    hideIfNoValue: PropTypes.bool,
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    type: PropTypes.string,
    readOnly: PropTypes.bool,
    selects: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.string,
    })),
    onChange: PropTypes.func,
    InputProps: PropTypes.object,
    validationError: PropTypes.string,
    autoFocus: PropTypes.bool,
    onClear: PropTypes.func,
    defaultText: PropTypes.string,
    multiline: PropTypes.bool,
    onEnter: PropTypes.func,
    inputSuffix: PropTypes.string,
    labelNoTranslate: PropTypes.string,
};

VimoInput.defaultProps = {
    className: '',
    hideIfNoValue: false,
    label: '',
    value: '',
    type: 'text',
    readOnly: false,
    selects: null,
    onChange: null,
    InputProps: null,
    validationError: null,
    onClear: null,
    autoFocus: false,
    defaultText: null,
    multiline: false,
    onEnter: null,
    inputSuffix: null,
    labelNoTranslate: null,
};

export default VimoInput;
